// ---------------------------------------------------------------------------
// The navigation model. Pure data and pure functions — no React, no DOM.
//
// It lives on its own so it can be asserted directly. This is the file that
// decides what each surface can reach, which makes it the last file in the
// product that should only be exercised by clicking around.
//
// THREE SURFACES, ONE CODEBASE (architecture doc §5). They differ by route
// prefix and by what a session may reach, never by build. A worker must not be
// able to type their way into the console, and the prefix is the first line of
// that — the session check at V3 is the real one. A prefix alone is not
// authorisation and is never treated as such.
// ---------------------------------------------------------------------------

export const SURFACES = ["console", "portal", "worker"] as const;
export type Surface = (typeof SURFACES)[number];

/** The single owner of each surface's URL prefix. Nothing else may spell these. */
export const SURFACE_PREFIX: Record<Surface, string> = {
  console: "/console",
  portal: "/portal",
  // Short, because a worker types or taps it on a phone in a car park.
  worker: "/app",
};

export type NavItem = {
  /** Stable identifier. Used by tests and by later slices' permission map. */
  id: string;
  /** What the user reads. Sentence case, never shouted. */
  label: string;
  /** Absolute path, always beginning with the surface prefix. */
  path: string;
  surface: Surface;
  /**
   * The permission id this destination needs, from `app.permission`.
   *
   * V4 adds this field and checks it against the database: verify-V4.sh reads
   * `app.permission` and fails if any `requires` value is not a real id. A typo
   * would hide a destination from every user forever and would present as a
   * permissions bug in the API, three slices away from its cause.
   *
   * HIDING A LINK IS NOT AUTHORISATION. The API refuses the request and the
   * database refuses the row; this only avoids offering somebody a door that
   * will be shut in their face.
   */
  requires: string;
};

/**
 * The one list of top-level destinations. Nothing else in the application may
 * invent one, because a destination that is not here cannot be permission-
 * gated, cannot be marked current, and cannot be checked by a script.
 *
 * V4 DELIBERATELY ADDS NO ENTRY FOR A SCREEN IT DOES NOT SHIP. A nav that
 * promises "Events" three slices before events exist is a list of ways to reach
 * a 404, and every one of them looks like a bug to the person who taps it.
 *
 * V5 ADDS TWO, AND BOTH HAVE SCREENS. Each `requires` names a permission the
 * database really holds — verify-V5.sh reads app.permission and fails on any id
 * that does not — and each names the READ permission rather than the manage
 * one, because a scheduler must be able to see who the work is for even though
 * they may not edit a client.
 */
export const NAV_ITEMS: NavItem[] = [
  { id: "console.home", label: "Overview", path: "/console", surface: "console", requires: "org.read" },
  { id: "console.clients", label: "Clients", path: "/console/clients", surface: "console", requires: "client.read" },
  // V6. `worker.read` and not `worker.manage`, for the reason V5 gave about
  // clients: a supervisor and a finance user must be able to see who is on the
  // bench without being offered a door to edit it.
  { id: "console.workers", label: "Bench", path: "/console/workers", surface: "console", requires: "worker.read" },
  { id: "console.people", label: "People", path: "/console/people", surface: "console", requires: "member.read" },
  // V7. `worker.read` and not `config.manage`: a scheduler must be able to SEE
  // what the organisation requires in order to understand why somebody is
  // blocked, and the screen itself hides the editing form from anybody without
  // config.manage. Naming the manage permission here would hide the whole
  // catalogue from the people who most need to read it.
  {
    id: "console.credentials",
    label: "Credentials",
    path: "/console/credential-types",
    surface: "console",
    requires: "worker.read",
  },
  // V8. `worker.read` for the same reason the credential catalogue uses it: a
  // scheduler must be able to SEE what a role requires in order to understand a
  // refusal, even though only config.manage may change it. Naming the manage
  // permission here would hide the explanation from the person the refusal is
  // addressed to.
  {
    id: "console.roles",
    label: "Roles",
    path: "/console/roles",
    surface: "console",
    requires: "worker.read",
  },
  // V9. TWO ENTRIES, BOTH WITH SCREENS — V4's rule that the nav never promises a
  // destination that does not exist yet.
  //
  // Configuration names `org.read` rather than `config.manage` for the reason
  // V7 and V8 both gave: a scheduler must be able to SEE what a pack requires in
  // order to understand a refusal, and the screen itself hides every editing
  // control from anybody without config.manage. Naming the manage permission
  // here would hide the explanation from the person the refusal is addressed to.
  {
    id: "console.configuration",
    label: "Configuration",
    path: "/console/configuration",
    surface: "console",
    requires: "org.read",
  },
  // Events names `event.read`, which is the read permission for the thing the
  // screen shows. Note that `event.read` is also held by client_viewer, an
  // EXTERNAL role — but client_viewer is a portal session and this entry is
  // scoped to the console surface, and the API's SELECT policy excludes external
  // actors from app.event entirely until V24 builds the client's own view.
  { id: "console.events", label: "Events", path: "/console/events", surface: "console", requires: "event.read" },
  { id: "console.hub-requests", label: "Requests from event.clinic", path: "/console/hub-requests", surface: "console", requires: "event.read" },
  // V24 RELABELS THIS AND DELIBERATELY DOES NOT RE-PERMISSION IT. The screen
  // behind it is now S6, the programme, so the label moves. `requires` stays
  // `event.read` because that is what session/access.ts gates the whole portal
  // surface on, and a nav entry stricter than its own surface produces a signed-in
  // client looking at a surface with no navigation on it — which reads as broken
  // rather than as unpermitted. Narrowing the SURFACE gate to `portal.read` is
  // the change that would actually be worth making and it belongs to whoever
  // revisits access.ts, not to a screens slice.
  // V25. THE AGENCY'S DELIVERY LEDGER. `notification.read` and not
  // `notification.send`, for the reason V7, V8 and V9 all gave: a scheduler must
  // be able to SEE what was sent, held or not sent in order to answer "did they
  // get it", and the drain button is hidden from anybody without send.
  // V30. THE COMPLIANCE CENTRE. `compliance.read`, and NOT `org.read` as the
  // other read-only catalogues use: the queue on this screen names decisions
  // taken about individual people, and `compliance.read` is the permission that
  // already gates the credential-override log and the Proof Pack for that
  // reason. The transparency register itself needs no permission at all and the
  // API asks for none — a worker must be able to read it, which is the
  // obligation — but a nav entry gates a SCREEN, and this screen shows more.
  {
    id: "console.compliance",
    label: "Compliance",
    path: "/console/compliance-centre",
    surface: "console",
    requires: "compliance.read",
  },
  // SWEEP S3, 3 OCTOBER 2026. FOUR ORGANISATION-WIDE SCREENS EXISTED WITH NO WAY IN.
  // Each names the permission its own API refuses without, so the entry appears for
  // exactly the people the screen will serve. Event-level screens are linked from the
  // event workspace rather than here: they need an event.
  { id: "console.analytics", label: "Scorecard", path: "/console/analytics", surface: "console", requires: "analytics.read" },
  { id: "console.people.appeals", label: "Appeals", path: "/console/people/appeals", surface: "console", requires: "worker.read" },
  // Sweep §4 / 0215. worker.manage, not worker.read: accepting puts a person on the bench.
  { id: "console.people.applicants", label: "Applicants", path: "/console/people/applicants", surface: "console", requires: "worker.manage" },
  { id: "console.sync", label: "Import and sync", path: "/console/sync", surface: "console", requires: "worker.read" },
  { id: "console.privacy", label: "Privacy", path: "/console/privacy-centre", surface: "console", requires: "privacy.manage" },
  // S20. org.manage is owner and admin — the two roles organisation_update admits.
  { id: "console.settings", label: "Organisation", path: "/console/settings", surface: "console", requires: "org.manage" },
  // Sweep §4 / 0234. api_key.manage — owner and admin — the permission every route behind it asks for.
  { id: "console.settings.api", label: "API and webhooks", path: "/console/settings/api", surface: "console", requires: "api_key.manage" },
  // ★ LAST IN THE MENU AND GATED ON org.read, which every internal console role
  // holds and no external one does — the same gate Overview uses. A help screen
  // a person cannot reach because of a permission would be a joke this product
  // does not need to make.
  { id: "console.help", label: "Help", path: "/console/help", surface: "console", requires: "org.read" },
  // Tour T-22. Reached from the header's account menu, so it is in the header group.
  { id: "console.account", label: "Your account", path: "/console/account", surface: "console", requires: "org.read" },
  {
    id: "console.notifications",
    label: "Notifications",
    path: "/console/notifications",
    surface: "console",
    requires: "notification.read",
  },
  { id: "portal.home", label: "Programme", path: "/portal", surface: "portal", requires: "event.read" },
  // V25. S7 — THE CLIENT'S OWN ACCOUNT SCREEN, deferred here from V24 because
  // every control on it writes a preference this slice owns.
  //
  // `requires` MATCHES THE SURFACE GATE, exactly as `portal.home` does and for
  // the reason recorded there: a nav entry stricter than its own surface
  // produces a signed-in client looking at a surface with no navigation on it,
  // which reads as broken rather than as unpermitted. And this one in particular
  // must be reachable by every client user without exception — a person who
  // cannot reach their own notification settings cannot turn off a message that
  // arrives at three in the morning.
  // S3. The marketplace screen, reachable on the same gate as the rest of the portal.
  { id: "portal.workers", label: "Who is free?", path: "/portal/workers", surface: "portal", requires: "event.read" },
  { id: "portal.account", label: "Your account", path: "/portal/account", surface: "portal", requires: "event.read" },
  { id: "worker.home", label: "My shifts", path: "/app", surface: "worker", requires: "assignment.read_own" },
  // S3. "My shifts" tells a worker to keep their availability current; this is where.
  { id: "worker.availability", label: "When I can work", path: "/app/availability", surface: "worker", requires: "self.read" },
  // V28. THE APPEAL ROUTE, IN THE NAV RATHER THAN BEHIND A URL.
  //
  // `self.read` matches what the endpoint asks for and is held by every signed-in
  // person, which is deliberate: a reliability record decides who is offered work,
  // so a route to contest it that some workers cannot see is not a route. Naming a
  // stricter permission here would hide the door from exactly the people the
  // directive requires it for.
  { id: "worker.record", label: "My record", path: "/app/record", surface: "worker", requires: "self.read" },
  // Sweep §4 / 0179. `self.read` to see it; the endpoint decides what may be changed.
  { id: "worker.profile", label: "About me", path: "/app/profile", surface: "worker", requires: "self.read" },
  // Sweep §4 / 0190. The permission the endpoint asks for.
  { id: "worker.expenses", label: "My expenses", path: "/app/expenses", surface: "worker", requires: "expense.claim_own" },
];

/**
 * The destinations a surface offers.
 *
 * `permissions` is optional so that the structural tests — every path sits
 * under its surface's prefix — can ask the question without inventing a
 * session. Omitting it returns the surface's full list, which is a description
 * of the product, not a grant.
 */
export function navigationFor(surface: Surface, permissions?: readonly string[]): NavItem[] {
  return NAV_ITEMS.filter(
    (item) =>
      item.surface === surface && (permissions === undefined || permissions.includes(item.requires)),
  );
}

/**
 * Which surface a path belongs to, or null.
 *
 * SEGMENT-EXACT, NOT `startsWith`. `"/console-admin".startsWith("/console")` is
 * true, and a prefix test that answers "console" for a path the console does not
 * own is how an unrelated route inherits a surface's chrome — and, once V3 wires
 * session checks to the surface, its audience.
 */
export function surfaceForPath(pathname: string): Surface | null {
  for (const surface of SURFACES) {
    const prefix = SURFACE_PREFIX[surface];
    if (pathname === prefix) return surface;
    if (pathname.startsWith(prefix + "/")) return surface;
  }
  return null;
}

/** True when every nav entry sits under the prefix its surface owns. */
export function navItemsAreWellFormed(items: NavItem[] = NAV_ITEMS): boolean {
  return items.every((item) => surfaceForPath(item.path) === item.surface);
}

// ---------------------------------------------------------------------------
// TOUR T-13 (owner, 20 October 2026): THE CONSOLE'S TWO LEVELS.
//
// Twenty-odd destinations in one row scrolled sideways. The owner chose six
// groups across the top, and under them a second row with the active group's
// pages. Help and Notifications go to the header. Every console item belongs to
// exactly one group or to the header; the test asserts that, so a destination
// added next week cannot fall off the menu.
// ---------------------------------------------------------------------------

export const CONSOLE_GROUPS = [
  { id: "overview", label: "Overview", items: ["console.home"] },
  { id: "events", label: "Events", items: ["console.events", "console.hub-requests"] },
  { id: "crew", label: "Crew", items: ["console.workers", "console.people.applicants", "console.credentials", "console.people.appeals", "console.analytics"] },
  { id: "clients", label: "Clients", items: ["console.clients"] },
  { id: "compliance-checks", label: "Compliance", items: ["console.compliance", "console.privacy"] },
  { id: "settings", label: "Settings", items: ["console.settings", "console.people", "console.roles", "console.configuration", "console.sync", "console.settings.api"] },
] as const;

/** Console destinations that live in the header, not in a group. */
export const CONSOLE_HEADER_ITEMS = ["console.help", "console.notifications", "console.account"] as const;

export type NavGroup = { id: string; label: string; items: NavItem[] };

/** The groups this session can reach, each with only its permitted pages; empty groups are dropped. */
export function consoleGroupsFor(items: readonly NavItem[]): NavGroup[] {
  const byId = new Map(items.map((i) => [i.id, i]));
  return CONSOLE_GROUPS.map((g) => ({
    id: g.id,
    label: g.label,
    items: g.items.map((id) => byId.get(id)).filter((i): i is NavItem => i !== undefined),
  })).filter((g) => g.items.length > 0);
}

/**
 * The item a path is under: the LONGEST path that equals it or is a segment
 * prefix of it, so /console/people/appeals is Appeals, not People, and
 * /console/events/123 is Events.
 */
export function activeItemFor(pathname: string, items: readonly NavItem[]): NavItem | null {
  let best: NavItem | null = null;
  for (const item of items) {
    const hit = pathname === item.path || pathname.startsWith(item.path + "/");
    if (hit && (best === null || item.path.length > best.path.length)) best = item;
  }
  return best;
}
